/* ClarityGuidePage.jsx — guidance + exploration quiz view for ONE branch of
 * the Career Clarity pathway (/career-clarity/after-10th/guide and
 * /career-clarity/after-inter/guide). Same palette as each branch's entry
 * page; sections follow the JSON's section_order inside Accordions. */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ChevronLeft, GraduationCap, Briefcase } from 'lucide-react';
import Navbar from '../components/Navbar';
import PathwayDisclaimer from '../components/PathwayDisclaimer';
import { Accordion } from '../components/preview-ui/PreviewPrimitives';
import { useAuth } from '../lib/auth';
import ExplorationQuiz from '../components/clarity/ExplorationQuiz';
import {
  GenericSection, AutomationSection, RoutesSection, FieldExamplesSection, RegionalSection,
  QualificationSection, GroupsSection, ActionLinks, Notice, Chip, useSourceMap, labelFor,
} from '../components/clarity/GuideSections';

const API = process.env.REACT_APP_BACKEND_URL;

const BRANCH = {
  'after-10th': { eyebrow: 'Career Clarity · After 10th', back: '/career-clarity/after-10th', backLabel: 'Back to the 5 groups', Icon: GraduationCap,
    bg: 'from-amber-50 via-orange-50 to-rose-50', text: 'from-amber-700 to-rose-700',
    accent: { grad: 'from-amber-500 to-orange-500', shadow: 'shadow-amber-200', hover: 'hover:border-amber-300 hover:bg-amber-50/60', keyHover: 'group-hover:border-amber-300 group-hover:text-amber-700', dot: 'bg-amber-500' } },
  'after-inter': { eyebrow: 'Career Clarity · After Inter / Class 12', back: '/career-clarity', backLabel: 'Back to Career Clarity', Icon: Briefcase,
    bg: 'from-teal-50 via-white to-blue-50', text: 'from-teal-700 to-blue-700',
    accent: { grad: 'from-teal-500 to-blue-600', shadow: 'shadow-teal-200', hover: 'hover:border-teal-300 hover:bg-teal-50/60', keyHover: 'group-hover:border-teal-300 group-hover:text-teal-700', dot: 'bg-teal-500' } },
};

const TITLES = {
  start_here: 'Start here', national_context: 'One guide, different boards and local rules', simple_terms: 'A few words in plain language',
  routes: 'Study and training routes', skill_options: 'Skill options: the actual work', telangana_training_routes: 'Telangana training routes (regional)',
  essential_foundations: 'Essential foundations', before_paying: 'Before paying, ask', first_month_plan: 'Your first month plan',
  linked_pathways: 'Explore on Learnixta', field_examples: 'Field examples: how work could change', qualification_check: 'What have you completed?',
  quizzes: 'Explore your interests and check your decision', intermediate_groups: 'Regional group examples',
};

export default function ClarityGuidePage({ branch }) {
  const cfg = BRANCH[branch];
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!cfg) { navigate('/career-clarity', { replace: true }); return; }
    axios.get(`${API}/api/career-clarity/guidance/${branch}`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || 'Could not load the guide.'));
  }, [branch, cfg, user, navigate]);

  const sources = useSourceMap(data?.official_sources);
  const fields = useMemo(() => data?.field_examples?.map((f) => ({ id: f.id, title: f.title })), [data]);

  if (!cfg) return null;
  if (err) return <div className="min-h-screen bg-[#FFF9F1]"><Navbar /><div className="pt-28 text-center text-red-600" data-testid="guide-error">{err}</div></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#FFF9F1]"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;

  const render = (key) => {
    const d = data[key];
    if (!d) return null;
    switch (key) {
      case 'automation_clarity': return <AutomationSection data={d} sources={sources} />;
      case 'routes': return <RoutesSection data={d} sources={sources} linked={data.linked_pathways} />;
      case 'field_examples': return <FieldExamplesSection data={d} sources={sources} routes={data.routes} />;
      case 'regional_guidance': return <RegionalSection data={d} sources={sources} />;
      case 'qualification_check': return <QualificationSection data={d} routes={data.routes} />;
      case 'intermediate_groups': return <GroupsSection data={d} sources={sources} />;
      case 'linked_pathways': return <ActionLinks actions={d} testIdPrefix="guide-linked" />;
      case 'quizzes': return <ExplorationQuiz branch={branch} quizzes={d} fields={fields} accent={cfg.accent} />;
      case 'final_message': return <div className="space-y-4"><p className="text-sm text-gray-700 leading-relaxed">{d.text}</p><ActionLinks actions={d.actions} testIdPrefix="guide-final" /><p className="text-sm text-gray-700 leading-relaxed">{d.advice}</p></div>;
      default: return <GenericSection data={d} sources={sources} />;
    }
  };
  const titleOf = (key) => data[key]?.heading || data[key]?.title || TITLES[key] || labelFor(key);
  const sections = (data.section_order || []).filter((k) => k !== 'quizzes' || data.quizzes);

  return (
    <div className={`min-h-screen bg-gradient-to-br ${cfg.bg}`} data-testid={`guide-page-${branch}`}>
      <Navbar />
      <div className="pt-28 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-4xl mx-auto min-w-0">
          <Link to={cfg.back} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-teal-700 mb-4" data-testid="guide-back"><ChevronLeft size={16} /> {cfg.backLabel}</Link>

          <p className={`text-[11px] font-extrabold uppercase tracking-[0.18em] text-transparent bg-clip-text bg-gradient-to-r ${cfg.text} mb-2 flex items-center gap-2`}><cfg.Icon size={14} className="text-gray-700" /> {cfg.eyebrow}</p>
          <h1 className="text-2xl md:text-4xl font-extrabold text-gray-900 leading-tight tracking-tight break-words" data-testid="guide-title">{data.page.title}</h1>
          <p className="text-gray-600 text-sm md:text-base mt-3 leading-relaxed" data-testid="guide-subtitle">{data.page.subtitle}</p>
          <p className="text-gray-700 text-sm mt-2 leading-relaxed" data-testid="guide-intro">{data.page.introduction}</p>
          <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-gray-500"><span>Updated {data.meta.updated_on}</span><span>·</span><span className="break-words">{data.meta.audience_stage}</span></div>

          <div className="mt-5"><ActionLinks actions={data.page.primary_actions} testIdPrefix="guide-primary" /></div>

          <div className="mt-6"><Notice testId="guide-scenario-notice">{data.automation_clarity.label}: {data.automation_clarity.what_the_arrow_means}</Notice></div>

          <div className="mt-6 space-y-3" data-testid="guide-sections">
            {sections.map((key, i) => (
              <Accordion key={key} title={<span className="flex items-center gap-2 flex-wrap">{titleOf(key)}{key === 'telangana_training_routes' && <Chip tone="teal">Regional</Chip>}</span>} defaultOpen={i === 0 || key === 'quizzes'} testId={`guide-acc-${key}`}>
                <div className="pt-1" data-testid={`guide-section-${key}`}>{render(key)}</div>
              </Accordion>
            ))}
          </div>

          <PathwayDisclaimer />
        </div>
      </div>
    </div>
  );
}
